/**
 * PDP glasses upsell (replaces the old chain cross-sell).
 *
 * For each `.product-upsell` container:
 *  - Clicking ANYWHERE on a card (image, title, CTA) toggles its selected
 *    state — cards never link away from the PDP. Only one card can be selected
 *    at a time; picking another card unselects the previous one. The selected
 *    card keeps its white background and flips its CTA from "Choose" to
 *    "Added", while `has-selection` lets CSS veil the other cards in orange.
 *  - The eye button opens an in-page lightbox that previews every image of the
 *    product (data-images, swipeable) behind the same Choose CTA as the card,
 *    so shoppers can inspect the product without leaving the page.
 *  - The main PDP add-to-cart button keeps its own label; pdp-ajax-add.js reads
 *    the selected cards and adds the current product plus every selected upsell
 *    product in one /cart/add.js call.
 *  - The component only renders when the current product belongs to the active
 *    discount's collection. Below the deal quantity it offers a second pair,
 *    at or above it a chain, and the mode is decided server-side. If the cart
 *    crosses that threshold in-page the rendered mode no longer matches, so
 *    the block hides (count computed server-side in the upsell-source section,
 *    re-fetched on every `theme:cartchanged`).
 *  - The horizontal slider is fully self-contained (own arrow/track classes),
 *    so the global custom.js `x_scroll(document)` never touches this component.
 *  - In `recommendations` mode the cards are pulled from the hidden
 *    `upsell-source` section via the Product Recommendations
 *    Section Rendering API and injected client-side.
 */
(function () {
  var ARROW_LEFT =
    '<svg fill="none" height="12" viewBox="0 0 8 12" width="8" xmlns="http://www.w3.org/2000/svg" data-factor="-1"><path d="M7 1L0.999999 6L7 11" stroke="#111111"/></svg>';
  var ARROW_RIGHT =
    '<svg fill="none" height="12" viewBox="0 0 8 12" width="8" xmlns="http://www.w3.org/2000/svg" data-factor="1"><path d="M1 11.209L7 6.20898L1 1.20898" stroke="#111111"/></svg>';

  function hideComponent(el) {
    el.style.display = 'none';
  }

  // Keep the add-to-cart button honest about what it will add: once an upsell
  // card is picked the button submits a pack, so it must say two products. Both
  // the desktop button and the mobile sticky one carry .addToCart along with the
  // singular and plural labels as data attributes, so one pass covers both.
  // Scoped to the section, leaving a quick-buy drawer that renders the same
  // markup for another product untouched.
  function updateAddToCartLabel(container) {
    var scope = container.closest('.shopify-section') || document;
    var count = container.querySelectorAll('.upsell-card.selected').length;
    Array.prototype.forEach.call(scope.querySelectorAll('.addToCart'), function (btn) {
      var singular = btn.getAttribute('data-add-to-cart-text');
      var plural = btn.getAttribute('data-add-to-cart-text-plural');
      if (!singular) return;
      // The count is the pack size: the picked card plus the product on this page.
      btn.textContent =
        count > 0 && plural ? plural.replace('#', count + 1) : singular;
    });
  }

  // Flag the container so CSS can veil the cards that were not picked.
  function updateSelectionState(container) {
    container.classList.toggle(
      'has-selection',
      !!container.querySelector('.upsell-card.selected')
    );
    updateAddToCartLabel(container);
  }

  // Single selection: picking a card unselects the previous one.
  function selectCard(container, card) {
    var previous = container.querySelectorAll('.upsell-card.selected');
    Array.prototype.forEach.call(previous, function (other) {
      if (other !== card) other.classList.remove('selected');
    });
    card.classList.add('selected');
    updateSelectionState(container);
  }

  function toggleCard(container, card) {
    if (card.classList.contains('selected')) {
      card.classList.remove('selected');
      updateSelectionState(container);
    } else {
      selectCard(container, card);
    }
  }

  /* ---------------- Image preview lightbox ---------------- */

  var lightbox = null;

  function onLightboxKeydown(evt) {
    if (evt.key === 'Escape') closeLightbox();
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.remove();
    lightbox = null;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onLightboxKeydown);
  }

  function openLightbox(container, card) {
    closeLightbox();

    var images = [];
    try {
      images = JSON.parse(card.getAttribute('data-images') || '[]');
    } catch (e) {}
    if (!images.length) return;

    var chooseLabel = container.getAttribute('data-choose-label') || 'Choose';

    lightbox = document.createElement('div');
    lightbox.className = 'upsell-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');

    var slides = images
      .map(function (src, i) {
        return (
          '<div class="upsell-lightbox__slide"><img src="' + src + '" alt=""' +
          (i > 0 ? ' loading="lazy"' : '') +
          '></div>'
        );
      })
      .join('');
    var dots = images
      .map(function (_, i) {
        return '<span class="upsell-lightbox__dot' + (i === 0 ? ' active' : '') + '"></span>';
      })
      .join('');

    var arrows =
      '<button type="button" class="upsell-lightbox__arrow upsell-lightbox__arrow--prev disabled" aria-label="Previous image">' +
      ARROW_LEFT +
      '</button>' +
      '<button type="button" class="upsell-lightbox__arrow upsell-lightbox__arrow--next" aria-label="Next image">' +
      ARROW_RIGHT +
      '</button>';

    lightbox.innerHTML =
      '<div class="upsell-lightbox__backdrop"></div>' +
      '<div class="upsell-lightbox__panel">' +
      '<div class="upsell-lightbox__header">' +
      '<h3 class="upsell-lightbox__title"></h3>' +
      '<button type="button" class="upsell-lightbox__close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="upsell-lightbox__viewer">' +
      '<div class="upsell-lightbox__track no-scrollbar">' + slides + '</div>' +
      (images.length > 1 ? arrows : '') +
      '</div>' +
      (images.length > 1 ? '<div class="upsell-lightbox__dots">' + dots + '</div>' : '') +
      '<button type="button" class="upsell-lightbox__select"></button>' +
      '</div>';

    // Title / label as text (attribute values, never trusted as HTML).
    var title = lightbox.querySelector('.upsell-lightbox__title');
    title.textContent = card.getAttribute('data-card-title') || '';
    var variantLine = card.getAttribute('data-card-variant') || '';
    if (variantLine) {
      var em = document.createElement('em');
      em.textContent = variantLine;
      title.appendChild(document.createElement('br'));
      title.appendChild(em);
    }
    lightbox.querySelector('.upsell-lightbox__select').textContent = chooseLabel;

    document.body.appendChild(lightbox);
    // Lock scrolling on <body>, the way the theme's own modals do
    // (.cc-modal-visible). Hiding overflow on <html> instead stops <body>'s own
    // overflow from passing up to the viewport, which turns <body> into the
    // scroll container and unsticks the sticky product column behind the
    // lightbox, so it jumps and then snaps back on close.
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onLightboxKeydown);

    var track = lightbox.querySelector('.upsell-lightbox__track');
    var dotEls = lightbox.querySelectorAll('.upsell-lightbox__dot');
    var prevArrow = lightbox.querySelector('.upsell-lightbox__arrow--prev');
    var nextArrow = lightbox.querySelector('.upsell-lightbox__arrow--next');

    var updateNav = function () {
      var index = Math.round(track.scrollLeft / track.clientWidth);
      Array.prototype.forEach.call(dotEls, function (dot, i) {
        dot.classList.toggle('active', i === index);
      });
      if (prevArrow) prevArrow.classList.toggle('disabled', index <= 0);
      if (nextArrow) nextArrow.classList.toggle('disabled', index >= images.length - 1);
    };
    track.addEventListener('scroll', updateNav);

    if (prevArrow) {
      prevArrow.addEventListener('click', function () {
        track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
      });
    }
    if (nextArrow) {
      nextArrow.addEventListener('click', function () {
        track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
      });
    }

    lightbox.querySelector('.upsell-lightbox__backdrop').addEventListener('click', closeLightbox);
    lightbox.querySelector('.upsell-lightbox__close').addEventListener('click', closeLightbox);
    lightbox.querySelector('.upsell-lightbox__select').addEventListener('click', function () {
      engage(container, 'select');
      selectCard(container, card);
      closeLightbox();
    });
  }

  function setupSelection(container) {
    // Safety net: the card form must never submit (plus is type="button").
    container.addEventListener('submit', function (evt) {
      if (evt.target.closest && evt.target.closest('.upsell-card__form')) {
        evt.preventDefault();
      }
    });

    // One delegated click handler: magnifier opens the preview lightbox,
    // anywhere else on a card toggles its selection. No navigation ever.
    container.addEventListener('click', function (evt) {
      if (!evt.target.closest) return;

      var preview = evt.target.closest('.upsell-card__preview');
      if (preview && container.contains(preview)) {
        evt.preventDefault();
        engage(container, 'preview');
        openLightbox(container, preview.closest('.upsell-card'));
        return;
      }

      var card = evt.target.closest('.upsell-card');
      if (!card || !container.contains(card)) return;
      evt.preventDefault();
      engage(container, 'select');
      toggleCard(container, card);
    });
  }

  // The markup is rendered for one side of the deal threshold; once the cart
  // crosses it the other variant is needed, which only a page load can produce,
  // so hide rather than keep showing the wrong offer. The count is computed
  // server-side in the upsell-source section, so re-fetch it instead of counting
  // cart items (cart.js has no collection info).
  function refreshVisibility(container) {
    var source = document.querySelector('[data-upsell-source-id]');
    if (!source) return;

    var sectionId = source.getAttribute('data-upsell-source-id');
    var sep = location.search ? '&' : '?';
    fetch(location.pathname + location.search + sep + 'section_id=' + encodeURIComponent(sectionId))
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var el = tmp.querySelector('[data-upsell-source-id]');
        if (!el || el.getAttribute('data-deal-active') !== 'true') return;

        var count = parseInt(el.getAttribute('data-deal-count') || '0', 10);
        var threshold = parseInt(el.getAttribute('data-deal-threshold') || '2', 10);
        var isChains = container.getAttribute('data-upsell-mode') === 'chains';

        if (isChains ? count < threshold : count >= threshold) {
          hideComponent(container);
        }
      })
      .catch(function () {});
  }

  function setupSlider(container) {
    var track = container.querySelector('.product-upsell__track');
    var nav = container.querySelector('.product-upsell__arrows');
    if (!track || !nav) return;

    // Only show the arrows when the track actually overflows.
    if (track.scrollWidth <= track.clientWidth + 1) {
      nav.classList.add('hidden');
      return;
    }
    nav.classList.remove('hidden');

    var left = nav.querySelector('svg[data-factor="-1"]');
    var right = nav.querySelector('svg[data-factor="1"]');

    Array.prototype.forEach.call(nav.querySelectorAll('svg'), function (svg) {
      svg.addEventListener('click', function () {
        if (svg.classList.contains('disabled')) return;
        engage(container, 'arrow');
        var factor = parseInt(svg.getAttribute('data-factor'), 10) || 1;
        track.scrollBy({ left: factor * track.clientWidth * 0.9, behavior: 'smooth' });
      });
    });

    track.addEventListener('scroll', function () {
      if (window.MellerUpsellAnalytics) {
        window.MellerUpsellAnalytics.scrolled(container, track.scrollLeft);
      }
    });

    var update = function () {
      if (left) left.classList.toggle('disabled', track.scrollLeft <= 1);
      if (right) {
        right.classList.toggle(
          'disabled',
          track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
        );
      }
    };
    track.addEventListener('scroll', update);
    update();
  }

  function loadRecommendations(container) {
    var source = document.querySelector('[data-upsell-source-id]');
    if (!source) {
      hideComponent(container);
      return;
    }

    var sectionId = source.getAttribute('data-upsell-source-id');
    var recUrl = container.getAttribute('data-rec-url');
    var productId = container.getAttribute('data-product-id');
    var limit = container.getAttribute('data-rec-limit') || '8';

    var url =
      recUrl +
      '?section_id=' + encodeURIComponent(sectionId) +
      '&intent=related' +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=' + encodeURIComponent(limit);

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;

        var rendered = tmp.querySelector('[data-upsell-source-id]');
        var cards = rendered ? rendered.querySelectorAll('.upsell-card') : [];
        var track = container.querySelector('.product-upsell__track');
        if (!cards || cards.length === 0 || !track) {
          hideComponent(container);
          return;
        }

        Array.prototype.forEach.call(cards, function (card) {
          track.appendChild(card);
        });

        setupSlider(container);
      })
      .catch(function () {
        hideComponent(container);
      });
  }

  // GA4 A/B instrumentation. View tracking and per-widget params live in
  // upsell-analytics.js; here we only report interactions.
  function engage(container, action) {
    if (window.MellerUpsellAnalytics) window.MellerUpsellAnalytics.engage(container, action);
  }

  function init() {
    var containers = document.querySelectorAll('.product-upsell');
    Array.prototype.forEach.call(containers, function (container) {
      setupSelection(container);

      // Disappear in real time whenever the cart changes (main PDP add,
      // cart drawer, etc. — all dispatch theme:cartchanged).
      document.addEventListener('theme:cartchanged', function () {
        refreshVisibility(container);
      });

      if (container.getAttribute('data-source-mode') === 'recommendations') {
        loadRecommendations(container);
      } else {
        setupSlider(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
